import { useEffect, useRef, useState } from "react";

/**
 * Web Audio synthesized engine hum + tyre whoosh.
 * - Engine: sawtooth oscillator whose frequency rises with speed (60..260 Hz).
 * - Tyres: filtered white noise whose gain rises with speed.
 * - Suspended until the user toggles it on (browsers block autoplay).
 */
export default function useEngineSound() {
  const ctxRef = useRef(null);
  const nodesRef = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      try { nodesRef.current?.stopAll?.(); } catch (_) { /* noop */ }
      try { ctxRef.current?.close(); } catch (_) { /* noop */ }
    };
  }, []);

  const build = () => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    const ctx = new AudioCtx();

    // Master
    const master = ctx.createGain();
    master.gain.value = 0.0;
    master.connect(ctx.destination);

    // Engine: two detuned sawtooth oscillators for a thicker hum
    const engineGain = ctx.createGain();
    engineGain.gain.value = 0.0;
    engineGain.connect(master);

    const osc1 = ctx.createOscillator();
    osc1.type = "sawtooth";
    osc1.frequency.value = 70;

    const osc2 = ctx.createOscillator();
    osc2.type = "sawtooth";
    osc2.frequency.value = 70;
    osc2.detune.value = 8;

    const engineLP = ctx.createBiquadFilter();
    engineLP.type = "lowpass";
    engineLP.frequency.value = 900;
    engineLP.Q.value = 6;
    osc1.connect(engineLP);
    osc2.connect(engineLP);
    engineLP.connect(engineGain);

    // Tyre / wind whoosh: white noise -> bandpass -> gain
    const bufferSize = 2 * ctx.sampleRate;
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;
    noise.loop = true;

    const noiseBP = ctx.createBiquadFilter();
    noiseBP.type = "bandpass";
    noiseBP.frequency.value = 1200;
    noiseBP.Q.value = 0.8;

    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0.0;

    noise.connect(noiseBP);
    noiseBP.connect(noiseGain);
    noiseGain.connect(master);

    osc1.start();
    osc2.start();
    noise.start();

    const nodes = {
      ctx, master, engineGain, osc1, osc2, engineLP, noise, noiseBP, noiseGain,
      stopAll: () => {
        try { osc1.stop(); } catch (_) { /* noop */ }
        try { osc2.stop(); } catch (_) { /* noop */ }
        try { noise.stop(); } catch (_) { /* noop */ }
      },
    };
    ctxRef.current = ctx;
    nodesRef.current = nodes;
    return nodes;
  };

  const enable = async () => {
    if (enabled) return;
    let nodes = nodesRef.current;
    if (!nodes) nodes = build();
    if (!nodes) return;
    if (nodes.ctx.state === "suspended") await nodes.ctx.resume();
    nodes.master.gain.setTargetAtTime(0.28, nodes.ctx.currentTime, 0.05);
    setEnabled(true);
    setReady(true);
  };

  const disable = () => {
    const nodes = nodesRef.current;
    if (!nodes) { setEnabled(false); return; }
    nodes.master.gain.setTargetAtTime(0.0, nodes.ctx.currentTime, 0.05);
    setEnabled(false);
  };

  const toggle = () => (enabled ? disable() : enable());

  // Public API: update sound based on current speed (km/h)
  const setSpeed = (kmh) => {
    const nodes = nodesRef.current;
    if (!nodes || !enabled) return;
    const norm = Math.min(1, Math.max(0, kmh / 180));
    const now = nodes.ctx.currentTime;
    // Engine frequency 60 Hz idle -> 260 Hz top
    const targetFreq = 60 + norm * 200;
    nodes.osc1.frequency.setTargetAtTime(targetFreq, now, 0.08);
    nodes.osc2.frequency.setTargetAtTime(targetFreq * 1.02, now, 0.08);
    // Engine gain 0.06 idle -> 0.35 top
    nodes.engineGain.gain.setTargetAtTime(0.06 + norm * 0.29, now, 0.08);
    // Wind rises sharply after 30%
    const windAmt = Math.max(0, norm - 0.15) * 0.55;
    nodes.noiseGain.gain.setTargetAtTime(windAmt, now, 0.1);
    nodes.noiseBP.frequency.setTargetAtTime(700 + norm * 2200, now, 0.1);
  };

  return { enabled, ready, enable, disable, toggle, setSpeed };
}
